import { computed, reactive, Ref, watch } from "vue";

// In-memory search + pagination for lists fetched in one go (zones, villes, agences, packs…).
// `pager` has the same shape as a Laravel paginator so it plugs straight into <PaginatorContent>.
export function useClientTable<T extends Record<string, any>>(
  items: Ref<T[]>,
  searchTerm: Ref<string>,
  perPage = 10
) {
  const state = reactive({ page: 1 });

  function matches(value: any, term: string): boolean {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some((v) => matches(v, term));
    if (typeof value === "object") return Object.values(value).some((v) => matches(v, term));
    return String(value).toLowerCase().includes(term);
  }

  const filtered = computed(() => {
    const term = searchTerm.value.trim().toLowerCase();
    if (!term) return items.value;
    return items.value.filter((row) => matches(row, term));
  });

  const pager = computed(() => {
    const total = filtered.value.length;
    const last_page = Math.max(1, Math.ceil(total / perPage));
    const current_page = Math.min(state.page, last_page);
    const from = total ? (current_page - 1) * perPage + 1 : 0;
    const to = Math.min(current_page * perPage, total);
    return { current_page, last_page, per_page: perPage, total, from, to };
  });

  const pagedRows = computed(() =>
    filtered.value.slice(pager.value.from ? pager.value.from - 1 : 0, pager.value.to)
  );

  function goToPage(page: number) {
    state.page = page;
  }

  watch(searchTerm, () => (state.page = 1));

  return { pagedRows, pager, goToPage };
}
